import { ReactNode, useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'

import { getCurrentUser } from '../../selectors/user.selectors'
import { useSidebarConfig } from '../Navigation/config'
import { useReselector } from '../../utils/sharedHooks'
import { useAppDispatch } from '../../utils/typeHelpers'
import { logoutUser } from '../../actions/authActions'
import { useAnalyticsReset } from '../../features/Amplitude'
import { clearOpenFeatureTargetingKey } from '../../features/OpenFeature'

import { layoutStyles, mainContentStyles } from './layoutStyles'
import { mtc } from '@/styles'
import { Sidebar } from '@/ui'
import {
  FETCH_USER_PERMISSIONS_KEY,
  getUserPermissions,
} from '@/actions/permissionActions'
import { useRouteMapper } from './useRouteMapper'
import { selectUserHasPermissionsForRoute } from '@/reducers/auth/permission.selectors'
import { getIsFetching } from '@/reducers/fetch'
import { useRouteConfig } from '@/utils/routeHelpers'
import { RouteAccessDenied } from '@/v2-components/Routes/RouteAccessDenied'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '@/v2-features/open-feature'

interface LayoutProps {
  children?: ReactNode
}

export const Layout = ({ children }: LayoutProps) => {
  const user = useReselector(getCurrentUser)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const resetAnalytics = useAnalyticsReset()
  const categories = useSidebarConfig()
  const { ALL_ROUTES } = useRouteMapper()
  const currentRoute = useRouteConfig(ALL_ROUTES)
  // TODO: Update as part of https://linear.app/heard/issue/ZEN-2064/remove-backwards-compatibility-for-priceproduct-id-checks
  const isPermissionCheckEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableStripeEntitlementPermissionChecks,
    false
  )
  const userHasPermissionToViewRoute = useReselector(
    selectUserHasPermissionsForRoute,
    ALL_ROUTES,
    currentRoute.key
  )
  const isFetchingPermissions = useReselector(
    getIsFetching,
    FETCH_USER_PERMISSIONS_KEY
  )
  const routeAccessDeniedModalOpen = useMemo(
    () =>
      !userHasPermissionToViewRoute &&
      !isFetchingPermissions &&
      isPermissionCheckEnabled,
    [
      userHasPermissionToViewRoute,
      isFetchingPermissions,
      isPermissionCheckEnabled,
    ]
  )

  useEffect(() => {
    dispatch(getUserPermissions())
  }, [dispatch])

  const handleLogoutClick = useCallback(async () => {
    resetAnalytics()
    await logoutUser()(dispatch)
    localStorage.removeItem('targetLink')
    await clearOpenFeatureTargetingKey()
    navigate('/login')
  }, [dispatch, navigate, resetAnalytics])

  const dropdownOptions = [
    {
      icon: regular('gear'),
      onClick: () => navigate('/accounts'),
      text: 'Settings',
    },
    {
      icon: regular('right-from-bracket'),
      onClick: handleLogoutClick,
      text: 'Logout',
    },
  ]

  return (
    <div className={mtc(layoutStyles())}>
      <Sidebar
        device={'desktop'}
        dropdownOptions={dropdownOptions}
        categories={categories}
        user={user || null}
      />

      <main
        className={mtc(mainContentStyles())}
        style={{
          boxShadow: '0 1px 2px rgba(18, 55, 105, 0.078)',
        }}
      >
        {/* <Alerts />*/}
        <div className="w-full max-w-[1200px] mx-auto">
          {routeAccessDeniedModalOpen ? (
            <RouteAccessDenied
              customComponent={
                currentRoute?.config?.requiredPermissions
                  ?.customPermissionComponent
              }
            />
          ) : (
            children
          )}
        </div>
      </main>
    </div>
  )
}
